/**
 * Service de gestion des notifications en temps réel
 * Compatible avec le backend Django via WebSocket/SSE
 */

import { ref, computed } from 'vue'
import { apiClient } from './apiClient'

export interface Notification {
  id: string
  user_id: string
  type: 'rdv' | 'urgence' | 'planning' | 'system' | 'reminder' | 'document'
    | 'cr_refuse' | 'cr_valide' | 'cr_en_attente'
    | 'nouvel_examen' | 'examen_urgent' | 'examen_realise'
    | 'retard_interpretation' | 'retard_validation'
    | 'equipement_panne' | 'maintenance_programmee'
  title: string
  message: string
  is_read: boolean
  is_urgent: boolean
  created_at: string
  data?: any // Données contextuelles (URLs, IDs, etc.)
}

export interface NotificationPreferences {
  desktop_notifications: boolean
  sound_notifications: boolean
  email_notifications: boolean
  urgent_only: boolean
  types: {
    rdv: boolean
    urgence: boolean
    planning: boolean
    system: boolean
    reminder: boolean
    document: boolean
    [key: string]: boolean
  }
}

class NotificationService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectTimeout: number | null = null
  private isConnecting = false
  private pollingInterval: ReturnType<typeof setInterval> | null = null
  private readonly POLL_INTERVAL_MS = 30_000

  // État réactif
  public notifications = ref<Notification[]>([])
  public isConnected = ref(false)
  public connectionStatus = ref<'connected' | 'connecting' | 'disconnected'>('disconnected')
  public preferences = ref<NotificationPreferences>({
    desktop_notifications: true,
    sound_notifications: true,
    email_notifications: false,
    urgent_only: false,
    types: {
      rdv: true,
      urgence: true,
      planning: true,
      system: true,
      reminder: true,
      document: true
    }
  })

  // Computed properties
  public unreadCount = computed(() => 
    this.notifications.value.filter(n => !n.is_read).length
  )

  public urgentCount = computed(() =>
    this.notifications.value.filter(n => !n.is_read && n.is_urgent).length
  )

  public unreadByType = computed(() => {
    const counts: { [key: string]: number } = {}
    this.notifications.value
      .filter(n => !n.is_read)
      .forEach(n => {
        counts[n.type] = (counts[n.type] || 0) + 1
      })
    return counts
  })

  /**
   * Initialise la connexion WebSocket
   */
  public async initialize(userId: string, token: string) {
    try {
      // Charger les notifications existantes
      await this.loadNotifications()

      // Charger les préférences
      await this.loadPreferences()

      // Établir connexion WebSocket
      this.connect(userId, token)

      // Demander permission pour notifications desktop
      await this.requestDesktopPermission()

      // Polling de secours (30 s) — garantit les nouvelles notifs même sans WebSocket
      if (!this.pollingInterval) {
        this.pollingInterval = setInterval(() => {
          this.loadNotifications().catch(() => {})
        }, this.POLL_INTERVAL_MS)
      }

    } catch (error) {
      console.error('Erreur initialisation notifications:', error)
    }
  }

  /**
   * Établit la connexion WebSocket
   */
  private connect(userId: string, token: string) {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    this.isConnecting = true
    
    // URL WebSocket - s'adapte à l'environnement
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = import.meta.env.DEV
      ? 'localhost:8000'
      : window.location.host
    const wsUrl = `${wsProtocol}//${wsHost}/ws/notifications/${userId}/?token=${token}`

    try {
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        console.log('🔔 Connexion WebSocket notifications établie')
        this.isConnected.value = true
        this.connectionStatus.value = 'connected'
        this.isConnecting = false
        this.reconnectAttempts = 0
        
        // Envoyer heartbeat périodique
        this.startHeartbeat()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleWebSocketMessage(data)
        } catch (error) {
          console.error('Erreur parsing message WebSocket:', error)
        }
      }

      this.ws.onclose = (event) => {
        console.log('🔔 Connexion WebSocket fermée:', event.code, event.reason)
        this.isConnected.value = false
        this.connectionStatus.value = 'disconnected'
        this.isConnecting = false
        
        // Reconnexion automatique si pas intentionnelle
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect(userId, token)
        }
      }

      this.ws.onerror = (error) => {
        console.error('🔔 Erreur WebSocket notifications:', error)
        this.isConnected.value = false
        this.connectionStatus.value = 'disconnected'
        this.isConnecting = false
      }

    } catch (error) {
      console.error('Erreur création WebSocket:', error)
      this.isConnecting = false
    }
  }

  /**
   * Gère les messages WebSocket reçus
   */
  private handleWebSocketMessage(data: any) {
    switch (data.type) {
      case 'notification':
        this.addNotification(data.notification)
        break
        
      case 'notification_read':
        this.markAsRead(data.notification_id)
        break
        
      case 'bulk_read':
        this.markMultipleAsRead(data.notification_ids)
        break
        
      case 'heartbeat':
        // Répondre au heartbeat
        this.sendHeartbeat()
        break
        
      default:
        console.log('Message WebSocket non géré:', data)
    }
  }

  /**
   * Ajoute une nouvelle notification
   */
  private addNotification(notification: Notification) {
    // Vérifier si déjà présente
    const exists = this.notifications.value.find(n => n.id === notification.id)
    if (exists) return

    // Vérifier préférences utilisateur
    if (!this.shouldShowNotification(notification)) return

    // Ajouter en début de liste
    this.notifications.value.unshift(notification)

    // Limiter le nombre de notifications en mémoire
    if (this.notifications.value.length > 100) {
      this.notifications.value = this.notifications.value.slice(0, 100)
    }

    // Afficher notification desktop/son
    this.showDesktopNotification(notification)
    this.playNotificationSound(notification)
  }

  /**
   * Vérifie si la notification doit être affichée selon les préférences
   */
  private shouldShowNotification(notification: Notification): boolean {
    const prefs = this.preferences.value
    
    // Si urgent seulement et pas urgent
    if (prefs.urgent_only && !notification.is_urgent) {
      return false
    }
    
    // Vérifier le type
    return prefs.types[notification.type] !== false
  }

  /**
   * Affiche une notification desktop
   */
  private showDesktopNotification(notification: Notification) {
    if (!this.preferences.value.desktop_notifications) return
    if (!('Notification' in window)) return
    if (Notification.permission !== 'granted') return

    const options: NotificationOptions = {
      body: notification.message,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notification.id,
      requireInteraction: notification.is_urgent,
      silent: !this.preferences.value.sound_notifications
    }

    const desktopNotif = new Notification(notification.title, options)
    
    desktopNotif.onclick = () => {
      window.focus()
      this.handleNotificationClick(notification)
      desktopNotif.close()
    }

    // Auto-fermeture pour notifications non urgentes
    if (!notification.is_urgent) {
      setTimeout(() => desktopNotif.close(), 5000)
    }
  }

  /**
   * Joue un son pour la notification
   */
  private playNotificationSound(notification: Notification) {
    if (!this.preferences.value.sound_notifications) return

    try {
      const audio = new Audio(
        notification.is_urgent 
          ? '/sounds/urgent-notification.mp3'
          : '/sounds/notification.mp3'
      )
      audio.volume = notification.is_urgent ? 0.8 : 0.5
      audio.play().catch(console.warn)
    } catch (error) {
      console.warn('Impossible de jouer le son de notification:', error)
    }
  }

  /**
   * Gère le clic sur une notification
   */
  private handleNotificationClick(notification: Notification) {
    // Marquer comme lue
    this.markAsRead(notification.id)

    // Navigation selon le type et les données
    if (notification.data?.url) {
      window.location.href = notification.data.url
    } else {
      switch (notification.type) {
        case 'rdv':
          if (notification.data?.rdv_id) {
            window.location.href = `/rendez-vous/${notification.data.rdv_id}`
          }
          break
        case 'planning':
          window.location.href = '/medecins/planning'
          break
        case 'document':
          if (notification.data?.document_id) {
            window.location.href = `/documents/${notification.data.document_id}`
          }
          break
        case 'cr_refuse':
          if (notification.data?.compte_rendu_id) {
            window.location.href = `/imagerie/transcription/${notification.data.compte_rendu_id}`
          } else if (notification.data?.examen_id) {
            window.location.href = `/imagerie/examens/${notification.data.examen_id}`
          }
          break
        case 'cr_en_attente':
        case 'cr_valide':
          if (notification.data?.examen_id) {
            window.location.href = `/imagerie/examens/${notification.data.examen_id}`
          }
          break
        case 'examen_realise':
        case 'nouvel_examen':
        case 'examen_urgent':
          if (notification.data?.examen_id) {
            window.location.href = `/imagerie/examens/${notification.data.examen_id}`
          }
          break
        default:
          // Ouvrir le centre de notifications
          this.openNotificationCenter()
      }
    }
  }

  /**
   * Charge les notifications existantes depuis l'API
   */
  public async loadNotifications(page = 1, limit = 50) {
    try {
      const response = await apiClient.get('/notifications/', {
        page,
        limit,
        ordering: '-created_at'
      })
      
      const newNotifications = response.data.results || response.data
      
      if (page === 1) {
        this.notifications.value = newNotifications
      } else {
        this.notifications.value.push(...newNotifications)
      }
      
      return response.data
    } catch (error) {
      console.error('Erreur chargement notifications:', error)
      throw error
    }
  }

  /**
   * Marque une notification comme lue
   */
  public async markAsRead(notificationId: string) {
    try {
      // Mise à jour optimiste
      const notification = this.notifications.value.find(n => n.id === notificationId)
      if (notification) {
        notification.is_read = true
      }

      // Mise à jour API
      await apiClient.patch(`/notifications/${notificationId}/`, {
        is_read: true
      })

      // Notifier via WebSocket si connecté
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'mark_read',
          notification_id: notificationId
        }))
      }
      
    } catch (error) {
      console.error('Erreur marquage notification lue:', error)
      
      // Rollback optimiste en cas d'erreur
      const notification = this.notifications.value.find(n => n.id === notificationId)
      if (notification) {
        notification.is_read = false
      }
    }
  }

  /**
   * Marque plusieurs notifications comme lues
   */
  public async markMultipleAsRead(notificationIds: string[]) {
    try {
      // Mise à jour optimiste
      notificationIds.forEach(id => {
        const notification = this.notifications.value.find(n => n.id === id)
        if (notification) notification.is_read = true
      })

      // Mise à jour API
      await apiClient.post('/notifications/mark-multiple-read/', {
        notification_ids: notificationIds
      })

    } catch (error) {
      console.error('Erreur marquage multiple notifications:', error)
      
      // Rollback
      notificationIds.forEach(id => {
        const notification = this.notifications.value.find(n => n.id === id)
        if (notification) notification.is_read = false
      })
    }
  }

  /**
   * Marque toutes les notifications comme lues
   */
  public async markAllAsRead() {
    const unreadIds = this.notifications.value
      .filter(n => !n.is_read)
      .map(n => n.id)
    
    if (unreadIds.length > 0) {
      await this.markMultipleAsRead(unreadIds)
    }
  }

  /**
   * Supprime une notification
   */
  public async deleteNotification(notificationId: string) {
    try {
      await apiClient.delete(`/notifications/${notificationId}/`)
      
      this.notifications.value = this.notifications.value.filter(
        n => n.id !== notificationId
      )
      
    } catch (error) {
      console.error('Erreur suppression notification:', error)
      throw error
    }
  }

  /**
   * Charge les préférences utilisateur
   */
  public async loadPreferences() {
    try {
      const response = await apiClient.get('/notification-preferences/')
      this.preferences.value = { ...this.preferences.value, ...response.data }
    } catch (error) {
      console.warn('Erreur chargement préférences notifications:', error)
      // Utiliser les préférences par défaut
    }
  }

  /**
   * Sauvegarde les préférences utilisateur
   */
  public async savePreferences(preferences: Partial<NotificationPreferences>) {
    try {
      this.preferences.value = { ...this.preferences.value, ...preferences }

      await apiClient.post('/notification-preferences/',
        this.preferences.value
      )
      
    } catch (error) {
      console.error('Erreur sauvegarde préférences:', error)
      throw error
    }
  }

  /**
   * Demande la permission pour les notifications desktop
   */
  private async requestDesktopPermission() {
    if (!('Notification' in window)) {
      console.warn('Notifications desktop non supportées')
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }

    return false
  }

  /**
   * Déconnecte le service
   */
  public disconnect() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.ws) {
      this.ws.close(1000, 'Déconnexion volontaire')
      this.ws = null
    }

    this.isConnected.value = false
    this.connectionStatus.value = 'disconnected'
  }

  /**
   * Ouvre le centre de notifications (à implémenter côté UI)
   */
  private openNotificationCenter() {
    // Sera implémenté avec le composant NotificationCenter
    console.log('Ouverture centre notifications')
  }

  /**
   * Planifie une reconnexion
   */
  private scheduleReconnect(userId: string, token: string) {
    this.reconnectAttempts++
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)
    
    console.log(`🔔 Reconnexion notifications prévue dans ${delay}ms (tentative ${this.reconnectAttempts})`)
    
    this.reconnectTimeout = window.setTimeout(() => {
      this.connect(userId, token)
    }, delay)
  }

  /**
   * Envoie un heartbeat
   */
  private sendHeartbeat() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'heartbeat' }))
    }
  }

  /**
   * Démarre le heartbeat périodique
   */
  private startHeartbeat() {
    setInterval(() => {
      this.sendHeartbeat()
    }, 30000) // Toutes les 30 secondes
  }
}

// Instance singleton
export const notificationService = new NotificationService()
export default notificationService